import { useState, useRef, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Option,
  tokens,
} from '@fluentui/react-components';
import { type UseMutationResult } from '@tanstack/react-query';
import { documentTypeDisplayNames } from '../../../types';

// Document types that are "dedicated" -- when uploading from a specific section,
// the type is implied and the user should NOT be prompted to pick one.
const dedicatedDocTypes = ['completed_return', 'unsigned_8879', 'signed_8879'];

export interface UploadDocumentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialDocType?: string;
  /** Existing lastFourEin from the tax return record (if already on file) */
  lastFourEin?: string;
  uploadMutation: UseMutationResult<unknown, Error, { file: File; docType: string; lastFourEin?: string }>;
}

export function UploadDocumentDialog({
  open,
  onOpenChange,
  initialDocType = 'other',
  lastFourEin: existingEin,
  uploadMutation,
}: UploadDocumentDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadDocType, setUploadDocType] = useState(initialDocType);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [einInput, setEinInput] = useState('');
  const [einError, setEinError] = useState('');

  // When the doc type is a dedicated type, skip the type selector
  const isDedicated = dedicatedDocTypes.includes(initialDocType);

  // Determine the effective doc type for the upload
  const effectiveDocType = isDedicated ? initialDocType : uploadDocType;

  // Whether this upload is a completed return (final tax return from Drake)
  const isCompletedReturn = effectiveDocType === 'completed_return';

  // Whether we need the user to enter the EIN
  const needsEinPrompt = isCompletedReturn && !existingEin;

  // Reset EIN input when dialog opens/closes
  useEffect(() => {
    if (open) {
      setEinInput('');
      setEinError('');
    }
  }, [open]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const validateEin = (value: string): boolean => {
    if (!/^\d{4}$/.test(value)) {
      setEinError('Please enter exactly 4 digits');
      return false;
    }
    setEinError('');
    return true;
  };

  const handleUpload = () => {
    if (!uploadFile) return;

    // Determine the EIN to send
    let einToSend: string | undefined;
    if (isCompletedReturn) {
      if (existingEin) {
        einToSend = existingEin;
      } else if (einInput) {
        if (!validateEin(einInput)) return;
        einToSend = einInput;
      }
      // EIN is optional -- the upload will still succeed without it,
      // but extraction may fail for password-protected PDFs
    }

    uploadMutation.mutate(
      { file: uploadFile, docType: effectiveDocType, lastFourEin: einToSend },
      {
        onSuccess: () => {
          onOpenChange(false);
          setUploadFile(null);
          setUploadDocType('other');
          setEinInput('');
          setEinError('');
        },
      }
    );
  };

  const handleOpenChange = (_: unknown, data: { open: boolean }) => {
    onOpenChange(data.open);
    if (!data.open) {
      setUploadFile(null);
      setUploadDocType(initialDocType);
      setEinInput('');
      setEinError('');
    }
  };

  const dialogTitle = isDedicated
    ? `Upload ${documentTypeDisplayNames[initialDocType] || initialDocType}`
    : 'Upload Document';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {isDedicated ? (
                <div style={{ fontSize: tokens.fontSizeBase300, color: 'var(--tl-text-secondary)', padding: '8px 0' }}>
                  Document type: <strong>{documentTypeDisplayNames[initialDocType] || initialDocType}</strong>
                </div>
              ) : (
                <Field label="Document Type">
                  <Dropdown
                    selectedOptions={[uploadDocType]}
                    value={documentTypeDisplayNames[uploadDocType] ?? uploadDocType}
                    onOptionSelect={(_, data) => setUploadDocType(data.optionValue as string)}
                    style={{ width: '100%' }}
                  >
                    {Object.entries(documentTypeDisplayNames).map(([value, label]) => (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Dropdown>
                </Field>
              )}

              {/* EIN prompt for completed returns -- only shown when EIN is NOT already on file */}
              {isCompletedReturn && !existingEin && (
                <>
                  <MessageBar intent="warning">
                    <MessageBarBody>
                      Drake final return PDFs are password-protected with the last 4 digits of the client's EIN. Enter it below so the system can unlock and analyze the return.
                    </MessageBarBody>
                  </MessageBar>
                  <Field
                    label="Last 4 of EIN"
                    validationState={einError ? 'error' : undefined}
                    validationMessage={einError || undefined}
                  >
                    <Input
                      value={einInput}
                      onChange={(_, data) => {
                        // Only allow digits, max 4
                        const cleaned = data.value.replace(/\D/g, '').slice(0, 4);
                        setEinInput(cleaned);
                        if (einError) setEinError('');
                      }}
                      placeholder="e.g. 1234"
                      maxLength={4}
                      style={{ width: '120px' }}
                    />
                  </Field>
                </>
              )}

              <Field label="File">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept=".pdf"
                  style={{ marginTop: '8px' }}
                />
              </Field>
              {uploadFile && (
                <div style={{ fontSize: tokens.fontSizeBase300, color: 'var(--tl-text-secondary)' }}>
                  Selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!uploadFile || uploadMutation.isPending || (needsEinPrompt && einInput.length < 4)}
              onClick={handleUpload}
            >
              {uploadMutation.isPending ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
